import clsx from 'clsx';
import React, { ChangeEvent } from 'react';
import { Trans, Translation } from 'react-i18next';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { PhoneNumber } from 'react-phone-number-input/index';

import { SelectPhoneField } from '@/components/Form/SelectPhoneField';
import { FieldOptions } from '@/types';

export const getCommonInputTextStyleClasses = () => 'md__payments__input--text';
export const getCommonInputTextAreaStyleClasses = () => 'md__payments__input--textarea';
export const getCommonInputSelectStyleClasses = () => 'md__payments__input--select';

export const getCheckboxTermsStyleClasses = () => 'md__payments__input--checkbox--terms';

export const getInputStyleClasses = (error: boolean, type: string) => {
  return clsx(getInputStyleClassesByType(type), {
    'md__payments__element--error': error,
    'md__payments__element--valid': !error,
  });
};

const getInputStyleClassesByType = (type: string): string => {
  switch (type) {
    case 'select':
      return getCommonInputSelectStyleClasses();
    case 'textarea':
      return getCommonInputTextAreaStyleClasses();
    case 'text':
    default:
      return getCommonInputTextStyleClasses();
  }
};

const getTagFormName = (formik: any, fieldId: string): string => {
  return formik?.initialValues?.formName
    ? `Forms.${formik.initialValues?.formName}.${fieldId}`
    : `Forms.${fieldId}`;
};

export const getFormName = (formik: any): string => {
  return formik?.initialValues?.formName;
};

export const renderField = (formik: any, fieldId: string, opts?: FieldOptions) => {
  const showLabel: boolean = opts?.showLabel !== false;

  const getInputField = () => (
    <input
      id={fieldId}
      name={opts?.name || fieldId}
      type={opts?.type || 'text'}
      value={opts?.value || formik?.values[fieldId] || ''}
      placeholder={opts?.type !== 'hidden' ? opts?.placeholder || '' : ''}
      className={`${
        opts?.type !== 'hidden'
          ? getInputStyleClasses(
              !!(formik?.touched[fieldId] && formik?.errors[fieldId]),
              opts?.type || 'text'
            )
          : ''
      } ${opts?.disabled ? 'md__payments__element--disabled' : ''}`}
      onChange={formik?.handleChange || opts?.onChange || false}
      onBlur={formik?.handleBlur || opts?.onBlur || false}
      disabled={opts?.disabled || false}
      // eslint-disable-next-line
      autoFocus={opts?.autoFocus || false}
    />
  );

  if (opts?.type === 'hidden') {
    return getInputField();
  }

  return (
    <div className={opts?.customClasses || 'md__payments__element--container'}>
      {showLabel && (
        <div className="md__payments__element--container--label flex items-center">
          <label htmlFor={fieldId} className="md__payments__element--valid">
            <Translation>{(t) => <span>{t(getTagFormName(formik, fieldId))}</span>}</Translation>
            {opts?.required && '*'}
            {opts?.helpText}
          </label>
          {formik ? (
            formik.touched[fieldId] && formik.errors[fieldId] ? (
              <div className="md__payments__element--container--error">
                {formik.errors[fieldId]}
              </div>
            ) : null
          ) : (
            ''
          )}
        </div>
      )}
      {getInputField()}
    </div>
  );
};

const getSelect = (formik: any, fieldId: string, opts?: FieldOptions) => {
  const onDefaultChange = (event: ChangeEvent<HTMLSelectElement>) => {
    opts && opts.onChange && opts.onChange(event, formik && formik.values);
    formik && formik.handleChange(event);
  };

  const onDefaultBlur = (event: ChangeEvent<HTMLSelectElement>) => {
    opts && opts.onBlur && opts.onBlur(event);
    formik && formik.handleBlur(event);
  };

  const defaultValue = opts?.value || formik?.values[fieldId] || '';

  return (
    <select
      id={fieldId}
      name={opts?.name || fieldId}
      placeholder={opts?.placeholder || ''}
      className={`${opts?.itemClasses} ${getInputStyleClasses(
        !!(formik?.touched[fieldId] && formik?.errors[fieldId]),
        'select'
      )} ${opts?.disabled ? 'md__payments__element--disabled' : ''}`}
      onChange={onDefaultChange}
      onBlur={onDefaultBlur}
      value={defaultValue}
      disabled={opts?.disabled || false}
    >
      <Translation>
        {(t) => (
          <option value="" label={t('Forms.select')}>
            {t('Forms.select')}
          </option>
        )}
      </Translation>

      {opts &&
        opts.options &&
        opts.options.map((value: any, index: any) => {
          const label = opts && opts.renderLabel ? opts.renderLabel(value) : value.name;

          return (
            <option
              key={index}
              value={value.id || value.value}
              label={label}
              className="capitalize"
            >
              {label}
            </option>
          );
        })}
    </select>
  );
};

const getPhoneSelect = (formik: any, fieldId: string, opts?: FieldOptions) => {
  const onDefaultChange = (
    event: ChangeEvent<HTMLInputElement> | string,
    phoneData: PhoneNumber | undefined
  ) => {
    const value = (typeof event === 'string' ? event : event?.target?.value) || '';
    opts && opts.onChange && opts.onChange(value, formik && formik.values, phoneData);
    formik && formik.setFieldValue(fieldId, value);
    formik && formik.handleChange(value);
  };

  const onDefaultBlur = (event: ChangeEvent<HTMLSelectElement> | string) => {
    const value = typeof event === 'string' ? event : event?.target?.value;
    formik && formik.handleBlur(value);
  };

  const defaultValue = opts?.value || formik?.values[fieldId] || '';

  return (
    <SelectPhoneField
      className={getInputStyleClasses(
        !!(formik?.touched[fieldId] && formik?.errors[fieldId]),
        'select'
      )}
      value={defaultValue}
      defaultCountry={opts?.defaultCountry}
      onChange={onDefaultChange}
      onBlur={onDefaultBlur}
      fieldId={fieldId}
      userLang={opts?.userLanguage}
    />
  );
};

const getSelectComponent = (formik: any, fieldId: string, opts?: FieldOptions) => {
  if (opts && opts.type === 'phone') {
    return getPhoneSelect(formik, fieldId, opts);
  }

  if (opts && opts.options && opts.options.length > 0) {
    return getSelect(formik, fieldId, opts);
  }

  return (
    <p className={clsx(getCommonInputTextStyleClasses(), 'md__payments__element--disabled')}>
      <Translation>{(t) => t('common.notAvailable')}</Translation>
    </p>
  );
};

export const renderSelect = (formik: any, fieldId: string, opts?: FieldOptions) => {
  const showLabel: boolean = opts?.showLabel !== false;

  return (
    <div className={opts?.customClasses || 'md__payments__element--container'}>
      {showLabel && (
        <div className="md__payments__element--container--label">
          <Translation>
            {(t) => <label htmlFor={fieldId}>{t(getTagFormName(formik, fieldId))}</label>}
          </Translation>
          {opts?.required && '*'}
          {formik ? (
            formik.touched[fieldId] && formik.errors[fieldId] ? (
              <div className="md__payments__element--container--error">
                {formik.errors[fieldId]}
              </div>
            ) : null
          ) : (
            ''
          )}
        </div>
      )}
      {getSelectComponent(formik, fieldId, opts)}
    </div>
  );
};

export const checkCP = (value: string | undefined, provinceId: string | undefined): boolean => {
  if (!value || !provinceId || value.length !== 5) {
    return false;
  }

  const provinceIdParsed = provinceId.padStart(2, '0');

  return value.slice(0, provinceIdParsed.length) === provinceIdParsed;
};

export const checkPhone = (value: string | undefined): boolean =>
  value ? isPossiblePhoneNumber(value) : false;

export const renderLegalCheck = (formik: any, fieldId: string, opts?: FieldOptions) => {
  const legalLabel = opts && opts.renderLabel ? opts.renderLabel() : '';
  const getInputLegalCheck = () => (
    <input
      id={fieldId}
      className={clsx(getCheckboxTermsStyleClasses(), {
        'md__payments__element--error': !!(formik.touched[fieldId] && formik.errors[fieldId]),
        'md__payments__element--valid': !(formik.touched[fieldId] && formik.errors[fieldId]),
      })}
      name={opts?.name || fieldId}
      type="checkbox"
      defaultChecked={false}
      onChange={formik?.handleChange || opts?.onChange || false}
      onBlur={formik?.handleBlur || opts?.onBlur || false}
    />
  );

  return (
    <div className={opts?.customClasses || 'md__payments__element--container'}>
      <label className="md__payments__element--valid" htmlFor={fieldId}>
        <div className="md__payments__input--checkbox--container-terms">
          {getInputLegalCheck()}
          <div className="md__payments__element--container--label">
            <Trans
              components={
                // eslint-disable-next-line
                { a: <a /> }
              }
            >
              {legalLabel}
              {opts?.required && '*'}
            </Trans>
            {formik.touched[fieldId] && formik.errors[fieldId] ? (
              <div className="md__payments__element--container--error">
                {formik.errors[fieldId]}
              </div>
            ) : null}
          </div>
        </div>
      </label>
    </div>
  );
};
